/**
 * @description Path + payload helpers for Super Admin Create / click-into under a CP (LYN-323 / LYN-325)
 *
 * Key features:
 * - Create route lives under Channel Partners → that CP → Sales Orgs → Create
 * - After-save return is that CP’s Sales Orgs tab
 * - Click-into reuses `/tenants/{salesOrgId}` (same TenantDetail). Back uses channelPartnerId
 * - Parent id is the Channel Partner on the page URL, never the overlay
 *
 * @dependencies none
 *
 * @notes
 * - Lives in utils/ — pages/ is PascalCase-only (lint-file-naming)
 * - Do not reuse LynxPay `/tenants/new` or `TenantCreate?channelPartnerId=`
 * - POST body uses channelPartnerId (column name). No soId / cpId aliases
 * - Overlay may be tagged; it does not choose the parent or the click-into address
 * - Configurations tab (LYN-308) is index 3. Sales Orgs / Merchants stay 1 / 2
 */

export const SALES_ORGS_TAB_QUERY = 'sales-orgs';

export const CP_DETAIL_TAB_QUERY = {
  overview: 0,
  'sales-orgs': 1,
  merchants: 2,
  configurations: 3,
  domain: 4,
  branding: 5,
  admins: 6
};

/**
 * @param {string} channelPartnerId
 * @param {string} [tab]
 * @returns {string}
 */
export function channelPartnerDetailPath(channelPartnerId, tab) {
  const base = `/channel-partners/${channelPartnerId}`;
  if (tab === SALES_ORGS_TAB_QUERY) {
    return `${base}?tab=${SALES_ORGS_TAB_QUERY}`;
  }
  return base;
}

/**
 * @param {string} channelPartnerId
 * @returns {string}
 */
export function createSalesOrgUnderCpPath(channelPartnerId) {
  return `/channel-partners/${channelPartnerId}/sales-orgs/new`;
}

/**
 * Existing Sales Org detail — LynxPay and under-CP share this address (LYN-325).
 * LynxPay `/tenants` list still hides under-CP rows.
 *
 * @param {string} salesOrgId
 * @returns {string}
 */
export function tenantDetailPath(salesOrgId) {
  return `/tenants/${salesOrgId}`;
}

/**
 * Back from reused TenantDetail. Under-CP returns to that partner’s Sales Orgs tab.
 * LynxPay keeps /tenants. Overlay is ignored.
 *
 * @param {{ channelPartnerId?: string|null } | null} tenant
 * @returns {string}
 */
export function tenantDetailBackPath(tenant) {
  const channelPartnerId = tenant?.channelPartnerId;
  if (channelPartnerId) {
    return channelPartnerDetailPath(channelPartnerId, SALES_ORGS_TAB_QUERY);
  }
  return '/tenants';
}

/**
 * @param {{ channelPartnerId?: string|null } | null} tenant
 * @returns {string}
 */
export function tenantDetailBackLabel(tenant) {
  return tenant?.channelPartnerId ? 'Back to Sales Orgs' : 'Back to Tenants';
}

/**
 * Parent id is the Channel Partner on the page URL.
 * Overlay is ignored even when passed.
 *
 * @param {string} urlChannelPartnerId
 * @param {{ channelPartnerId?: string } | null} [_overlay]
 * @returns {string|null}
 */
export function resolveCreateParentChannelPartnerId(urlChannelPartnerId, _overlay) {
  return urlChannelPartnerId || null;
}

/**
 * @param {URLSearchParams | { get?: Function }} searchParams
 * @returns {number}
 */
export function salesOrgsTabIndexFromSearch(searchParams) {
  const key = typeof searchParams?.get === 'function'
    ? searchParams.get('tab')
    : null;
  if (key && Object.prototype.hasOwnProperty.call(CP_DETAIL_TAB_QUERY, key)) {
    return CP_DETAIL_TAB_QUERY[key];
  }
  return 0;
}

/**
 * Today's create-SO identity fields plus required templateId.
 * No extra CP-only fields. No overlay id.
 *
 * @param {object} input
 * @returns {object}
 */
export function buildCreateSalesOrgUnderCpBody({
  organizationName,
  slug,
  templateId,
  adminEmail,
  adminFirstName,
  adminLastName,
  channelPartnerId,
  timezone,
  sendInvite
}) {
  return {
    organizationName,
    slug,
    templateId,
    adminEmail,
    adminFirstName,
    adminLastName,
    channelPartnerId,
    timezone,
    sendInvite: sendInvite === true
  };
}
